import React from 'react';
import { X as XIcon, Star, Plus, Trash2 } from 'lucide-react';

/**
 * BrokerProposalEditor — live side-drawer for purchase / refinance proposals.
 *
 * Mirrors the consolidation `ProposalEditor` API: receives the current
 * payload + an `onChange(patch | fn(prev) => next)` callback. Every keystroke
 * fires onChange so the parent can debounce-autosave + broadcast the new
 * payload to the guest viewer in real time.
 *
 * Edit surface:
 *  • Lender 1-3 (name / rate / type / repayment / features + recommended toggle)
 *  • Funds Required line items (purchase price, stamp duty, legal/lender fees,
 *    LMI, registration, plus any custom otherFees rows)
 *  • Funds Available (loan amount, deposit, gift, fhog, otherContributions)
 *  • Refinance-only: currentLoanBalance + debts list
 */
const Field = ({ label, value, onChange, type = 'text', step, placeholder }) => (
  <div className="mb-2">
    {label && <label className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 block mb-1">{label}</label>}
    <input
      type={type}
      step={step}
      value={value ?? ''}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border rounded px-2 py-1 text-xs"
    />
  </div>
);

const Toggle = ({ checked, onChange, label }) => (
  <label className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wide font-bold text-slate-600 cursor-pointer">
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="h-3 w-3 accent-emerald-600" />
    {label}
  </label>
);

const BrokerProposalEditor = ({ payload, onChange, onClose }) => {
  const lenders = Array.isArray(payload.lenders) && payload.lenders.length === 3
    ? payload.lenders
    : [
        payload.lenders?.[0] || { name: '', rate: 0, type: 'Variable', repayment: 'Principal & Interest', features: {}, recommended: true },
        payload.lenders?.[1] || { name: '', rate: 0, type: 'Variable', repayment: 'Principal & Interest', features: {}, recommended: false },
        payload.lenders?.[2] || { name: '', rate: 0, type: 'Variable', repayment: 'Principal & Interest', features: {}, recommended: false },
      ];
  const fr = payload.fundsRequired || {};
  const fa = payload.fundsAvailable || {};
  const debts = Array.isArray(payload.debts) ? payload.debts : [];
  const isRefi = payload.loanPurpose === 'refinance';

  const setLender = (i, patch) => onChange((p) => {
    const arr = (p.lenders || []).slice();
    while (arr.length < 3) arr.push({ name: '', rate: 0, type: 'Variable', repayment: 'Principal & Interest', features: {} });
    arr[i] = { ...arr[i], ...patch };
    return { ...p, lenders: arr };
  });
  const setLenderFeature = (i, key, val) => setLender(i, { features: { ...(lenders[i]?.features || {}), [key]: val } });
  const setRecommended = (idx) => onChange((p) => ({
    ...p,
    lenders: (p.lenders || lenders).map((l, i) => ({ ...l, recommended: i === idx })),
  }));
  const setFundsRequired = (patch) => onChange((p) => ({ ...p, fundsRequired: { ...(p.fundsRequired || {}), ...patch } }));
  const setFundsAvailable = (patch) => onChange((p) => ({ ...p, fundsAvailable: { ...(p.fundsAvailable || {}), ...patch } }));
  const setOtherFee = (i, patch) => onChange((p) => {
    const arr = ((p.fundsRequired || {}).otherFees || []).slice();
    arr[i] = { ...arr[i], ...patch };
    return { ...p, fundsRequired: { ...(p.fundsRequired || {}), otherFees: arr } };
  });
  const addOtherFee = () => onChange((p) => {
    const arr = ((p.fundsRequired || {}).otherFees || []).slice();
    arr.push({ name: '', amount: 0 });
    return { ...p, fundsRequired: { ...(p.fundsRequired || {}), otherFees: arr } };
  });
  const removeOtherFee = (i) => onChange((p) => {
    const arr = ((p.fundsRequired || {}).otherFees || []).slice();
    arr.splice(i, 1);
    return { ...p, fundsRequired: { ...(p.fundsRequired || {}), otherFees: arr } };
  });
  const setDebt = (i, patch) => onChange((p) => {
    const arr = (p.debts || []).slice();
    arr[i] = { ...arr[i], ...patch };
    return { ...p, debts: arr };
  });
  const addDebt = () => onChange((p) => ({ ...p, debts: [...(p.debts || []), { type: 'Personal Loan', name: '', amount: 0 }] }));
  const removeDebt = (i) => onChange((p) => {
    const arr = (p.debts || []).slice();
    arr.splice(i, 1);
    return { ...p, debts: arr };
  });

  return (
    <div
      className="absolute right-0 top-12 bottom-16 w-full sm:w-96 bg-white shadow-2xl border-l border-slate-200 overflow-y-auto z-20"
      data-testid="proposal-editor"
    >
      <div className="sticky top-0 bg-slate-100 border-b px-4 py-2 flex items-center justify-between z-10">
        <div className="text-xs uppercase tracking-wide font-bold text-slate-600">Live Editor — {isRefi ? 'Refinance' : 'Purchase'}</div>
        <button onClick={onClose} className="text-slate-500 hover:text-slate-700"><XIcon className="h-4 w-4" /></button>
      </div>

      <div className="p-4 space-y-5 text-sm">
        {/* ── Lenders ───────────────────────────────────────── */}
        <div>
          <p className="font-bold text-slate-700 mb-2">Lender options</p>
          {lenders.map((l, i) => (
            <div key={i} className={`border-2 rounded p-2.5 mb-2 ${l.recommended ? 'border-emerald-400 bg-emerald-50/50' : 'border-slate-200'}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Option {i + 1}</span>
                <button
                  onClick={() => setRecommended(i)}
                  data-testid={`set-recommended-${i}`}
                  className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded ${
                    l.recommended ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                  }`}
                >
                  <Star className="h-3 w-3" /> {l.recommended ? 'TOP PICK' : 'Set as top'}
                </button>
              </div>
              <Field label="Lender name" placeholder="e.g. CBA" value={l.name} onChange={(v) => setLender(i, { name: v })} />
              <div className="grid grid-cols-2 gap-2">
                <Field label="Rate %" type="number" step="0.01" value={l.rate} onChange={(v) => setLender(i, { rate: Number(v) || 0 })} />
                <Field label="Type" value={l.type || 'Variable'} onChange={(v) => setLender(i, { type: v })} />
              </div>
              <Field label="Repayment" value={l.repayment || 'Principal & Interest'} onChange={(v) => setLender(i, { repayment: v })} />
              <div className="flex flex-wrap gap-3 mt-1">
                <Toggle checked={l.features?.offset} onChange={(v) => setLenderFeature(i, 'offset', v)} label="Offset" />
                <Toggle checked={l.features?.redraw} onChange={(v) => setLenderFeature(i, 'redraw', v)} label="Redraw" />
                <Toggle checked={l.features?.extraPayments} onChange={(v) => setLenderFeature(i, 'extraPayments', v)} label="Extra Pmt" />
              </div>
            </div>
          ))}
        </div>

        {/* ── Funds Required ────────────────────────────────── */}
        <div>
          <p className="font-bold text-slate-700 mb-2">Funds Required</p>
          {!isRefi && (
            <Field label="Purchase price" type="number" value={fr.purchasePrice ?? ''} onChange={(v) => setFundsRequired({ purchasePrice: Number(v) || 0 })} />
          )}
          {isRefi && (
            <Field label="Current loan balance" type="number" value={payload.currentLoanBalance ?? ''} onChange={(v) => onChange({ currentLoanBalance: Number(v) || 0 })} />
          )}
          <div className="grid grid-cols-2 gap-2">
            <Field label="Stamp duty" type="number" value={fr.stampDuty ?? ''} onChange={(v) => setFundsRequired({ stampDuty: Number(v) || 0 })} />
            <Field label="Registration" type="number" value={fr.registration ?? ''} onChange={(v) => setFundsRequired({ registration: Number(v) || 0 })} />
            <Field label="Legal fees" type="number" value={fr.legalFees ?? ''} onChange={(v) => setFundsRequired({ legalFees: Number(v) || 0 })} />
            <Field label="Lender fees" type="number" value={fr.lenderFees ?? ''} onChange={(v) => setFundsRequired({ lenderFees: Number(v) || 0 })} />
            <Field label="LMI" type="number" value={fr.lmi ?? ''} onChange={(v) => setFundsRequired({ lmi: Number(v) || 0 })} />
          </div>
          <div className="mt-2">
            <div className="flex items-center justify-between mb-1">
              <p className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">Other fees</p>
              <button onClick={addOtherFee} className="text-[10px] text-emerald-600 font-semibold hover:underline" data-testid="add-other-fee">+ Add</button>
            </div>
            {(fr.otherFees || []).map((f, i) => (
              <div key={i} className="grid grid-cols-[1fr_80px_24px] gap-1 mb-1 items-center">
                <input className="border rounded px-2 py-1 text-xs" placeholder="Name" value={f.name || ''} onChange={(e) => setOtherFee(i, { name: e.target.value })} />
                <input type="number" className="border rounded px-2 py-1 text-xs" placeholder="$" value={f.amount || ''} onChange={(e) => setOtherFee(i, { amount: Number(e.target.value) || 0 })} />
                <button onClick={() => removeOtherFee(i)} className="text-rose-500"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        </div>

        {/* ── Funds Available ──────────────────────────────── */}
        <div>
          <p className="font-bold text-slate-700 mb-2">Funds Available</p>
          <Field label="Loan amount" type="number" value={fa.loanAmount ?? ''} onChange={(v) => setFundsAvailable({ loanAmount: Number(v) || 0 })} />
          {!isRefi && (
            <div className="grid grid-cols-2 gap-2">
              <Field label="Deposit" type="number" value={fa.deposit ?? ''} onChange={(v) => setFundsAvailable({ deposit: Number(v) || 0 })} />
              <Field label="Gift" type="number" value={fa.gift ?? ''} onChange={(v) => setFundsAvailable({ gift: Number(v) || 0 })} />
              <Field label="FHOG" type="number" value={fa.fhog ?? ''} onChange={(v) => setFundsAvailable({ fhog: Number(v) || 0 })} />
              <Field label="Other contrib." type="number" value={fa.otherContributions ?? ''} onChange={(v) => setFundsAvailable({ otherContributions: Number(v) || 0 })} />
            </div>
          )}
        </div>

        {/* ── Debts (refinance only) ──────────────────────────── */}
        {isRefi && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="font-bold text-slate-700">Debts being consolidated</p>
              <button onClick={addDebt} className="text-xs text-emerald-600 font-semibold hover:underline" data-testid="add-debt-btn">+ Add</button>
            </div>
            {debts.length === 0 && <p className="text-xs text-slate-400 italic">No debts yet</p>}
            {debts.map((d, i) => (
              <div key={i} className="border border-slate-200 rounded p-2 mb-2 space-y-1">
                <div className="flex items-center gap-2">
                  <input className="flex-1 border rounded px-2 py-1 text-xs" placeholder="Type (Personal Loan, CC...)" value={d.type || ''} onChange={(e) => setDebt(i, { type: e.target.value })} />
                  <button onClick={() => removeDebt(i)} className="text-rose-500"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
                <div className="grid grid-cols-2 gap-1">
                  <input className="border rounded px-2 py-1 text-xs" placeholder="Lender / detail" value={d.name || ''} onChange={(e) => setDebt(i, { name: e.target.value })} />
                  <input type="number" className="border rounded px-2 py-1 text-xs" placeholder="Amount" value={d.amount || ''} onChange={(e) => setDebt(i, { amount: Number(e.target.value) || 0 })} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BrokerProposalEditor;
